//#region import libraries
import React, { useEffect, useState } from 'react';

// select
import Select from "react-select";

// icons
import { FiRefreshCcw } from "react-icons/fi";
import { FaCirclePlus } from "react-icons/fa6";
import { FaSearch, FaCog } from "react-icons/fa";
import { BsAirplane } from "react-icons/bs";

// styling
import { Spacing, Colors } from '_styles';

// redux
import { useSelector, useDispatch } from "react-redux";
import { resetAddEmployeeForm } from "_redux/slices/timeline/employees";
import { selectUsedTheme } from '_redux/slices/timeline/theme';
import { selectStatus as selectTimelineFlightListStatus, setFlightSearchTerm, selectFlightSearchTerm } from '_redux/slices/timeline/timeline-flight-list';
import { selectStatus as selectTimelineBookingsStatus } from '_redux/slices/timeline/timeline-bookings';
import { increaseTimelineSlots, increaseTimelineStart, selectStartLimit, selectEndLimit, selectPreBoard, selectRampBookings } from '_redux/slices/timeline/timeline-control';
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';

// custom compoennts
import { CustomSpinner } from '_atoms';
import EmployeeCount from '../employee-components/employee-count';
import AddEmployeeForm from "../employee-components/add-employee-form";
import ActionsModal from '_components/molecules/Modals/actions-modal';
import TimelineSearch from '_components/organisms/timeline-dashboard/timeline-search';
import FlightEvents from '../flight-events';
import { FaPlus } from "react-icons/fa";
import { FaChevronRight, FaChevronLeft } from "react-icons/fa6";

// tremor
import { Button } from 'react-bootstrap';
import { selectBookingType, setBookingType, selectSelectedTimeline } from '_redux/slices/timeline/timeline-areas';


// utils
import { BookingTypesFilters } from "../timeline-constants";
// utils
import { useInterval } from '_utils/hooks';

//#endregion

const JobListToolbar = (props) => {

    //#region redux
    const dispatch = useDispatch();

    const usedTheme = useSelector(selectUsedTheme);
    const timelineFlightListStatus = useSelector(selectTimelineFlightListStatus);
    const timelineBookingsStatus = useSelector(selectTimelineBookingsStatus);
    const startLimit = useSelector(selectStartLimit);       // true if can no longer go backwards
    const endLimit = useSelector(selectEndLimit);           // true if can no longer go forwards
    const preBoard = useSelector(selectPreBoard);                       // status of auto accept unplaned task switch
    const rampBookings = useSelector(selectRampBookings);                   // status of RampBookings switch
    const selectedTimeline = useSelector(selectSelectedTimeline);            // the selected timeline
    const bookingType = useSelector(selectBookingType);                      // select bookingType (departures,arrivals,all)
    const dbUser = useSelector(selectFirebaseInfo);                          // logged in user
    //#endregion

    //#region local states
    const [lastUpdate, setLastUpdate] = useState(new Date())
    const [showAddModal, setShowAddModal] = useState(false);                 // show add employee modal
    const [showTimelineSearchModal, setShowTimelineSearchModal] = useState(false); // show timeline search modal
    const [showFlightEventsModal, setShowFlightEventsModal] = useState(false); // show flight events modal
    const [bookingsTypes, setBookingsTypes] = useState([]);                 // array of bookings types to preview(all, arrivals,....)
    const searchTerm = useSelector(selectFlightSearchTerm);                  // search term from Redux
    //#endregion

    //#region useEffect
    useEffect(() => {
        setLastUpdate(new Date());
    }, []);

    useEffect(() => {

        let tempBookingsTypes = BookingTypesFilters;
        if (!preBoard) {
            tempBookingsTypes = tempBookingsTypes.filter((type) => type.value !== "boarding");
        }
        if (!rampBookings) {
            tempBookingsTypes = tempBookingsTypes.filter((type) => type.value !== "ramp");
        }

        // check if no area was selected (selectedTimeline is null) but all the BookingTypesFilters in the bookingsTypes
        if (Object.isObjectEmpty(selectedTimeline)) {
            setBookingsTypes(tempBookingsTypes);

            dispatch(setBookingType(["all"]))
        } else {
            // if the selected area (selectedTimeline) is for arrivals show only arrivals 
            if (selectedTimeline?.arrivals === true && selectedTimeline?.departures !== true) {
                setBookingsTypes([
                    { label: "Arrivals", value: "arrivals" },
                    { label: "Ramp", value: "ramp" }
                ]);
            } else if (selectedTimeline?.arrivals !== true && selectedTimeline?.departures === true) {
                // if the selected area (selectedTimeline) is for departures show departures, checked in, ALTpickups
                setBookingsTypes([
                    { label: "Departures", value: "departures" },
                    { label: "ALT Pick Up", value: "ALTPickUp" },
                    preBoard && { label: "Boarding", value: "boarding" },
                    // { label: "Checked In", value: "checkedIn" }
                ]);

            } else {

                // show all filters
                setBookingsTypes(tempBookingsTypes);
                if (!Array.isFullArray(selectedTimeline?.jobsType)) {
                    dispatch(setBookingType(["all"]))
                }
            }
        }

    }, [selectedTimeline, preBoard, rampBookings]);

    //#endregion

    // auto refresh every minute
    useInterval(async () => {

        props?.handleGetBookings(bookingType);

        props?.handleGetFlights(bookingType);

        setLastUpdate(new Date());

    }, 60000);

    //#region functions
    // modal open handler
    const handleAddEmployee = () => {
        console.log("[CreateEmployee] toolbar — opening Create Employee Account modal");
        setShowAddModal(true); // show modal
    };

    // modal close handler
    const handleCloseModal = () => {
        console.log("[CreateEmployee] toolbar — closing Create Employee Account modal");
        dispatch(resetAddEmployeeForm()); // reset form for next use
        setShowAddModal(false); // close modal
    };

    // timeline search modal handlers
    const handleOpenTimelineSearch = () => {
        setShowTimelineSearchModal(true);
    };

    const handleCloseTimelineSearchModal = () => {
        setShowTimelineSearchModal(false);
    };

    const handleFlightSelect = (flight) => {
        // Close modal when a flight is selected
        handleCloseTimelineSearchModal();
        // Additional logic can be added here if needed
    };

    const handlePassengerSelect = (passenger) => {
        // Close modal when a passenger is selected
        handleCloseTimelineSearchModal();
        // Additional logic can be added here if needed
    };

    // filter the previewed booking by the type (all, departures, arrivals and ALTPikcUp)
    // const changeBookingTypeFilter = (selectedValue) => {
    //     if (Array.isFullArray(selectedValue)) {
    //         dispatch(setBookingType(selectedValue?.map((value) => value?.value)));
    //     } else dispatch(setBookingType(["all"]));
    // };
    const changeBookingTypeFilter = (selectedValue) => {
        if (Array.isFullArray(selectedValue)) {
            let tempSelectedValue = selectedValue?.map((value) => value?.value);
            if (tempSelectedValue?.length > 1 && tempSelectedValue?.includes("all")) {
                tempSelectedValue = tempSelectedValue?.filter((value) => value !== "all");
            }
            dispatch(setBookingType(tempSelectedValue));
        } else dispatch(setBookingType(["all"]));
    };

    const handleEmployeeSettings = () => {
        props?.openEmployeeSettings?.();
    };

    const createBookingTypeFilterObj = () => {
        let appliedFilter = BookingTypesFilters.filter((type) => bookingType?.includes(type?.value));
        return Array.isFullArray(appliedFilter) ? appliedFilter : []
    };
    //#endregion

    return (
        <div style={{ width: "100%", display: 'flex', justifyContent: 'space-between', flex: 1 }}>

            {/* pagination */}
            <div style={{ flex: 1 }}>
                <div style={{ justifyContent: "flex-start", display: "flex", alignItems: "center", flex: 3 }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.SCALE_8, marginLeft: Spacing.SCALE_18 }}>
                        <EmployeeCount />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: Spacing.SCALE_8 }}>
                        <FaCirclePlus onClick={handleAddEmployee} style={{ cursor: "pointer", marginBottom: Spacing.SCALE_10 }} className='mt-2' color={usedTheme.JOB_LIST_COLOR} size={24} data-tippy-content="Add Employee" />
                        <FaCog onClick={handleEmployeeSettings} style={{ cursor: "pointer", marginLeft: Spacing.SCALE_8, marginBottom: Spacing.SCALE_10 }} className='mt-2' color={usedTheme.JOB_LIST_COLOR} size={22} data-tippy-content="Employee Settings" />
                    </div>
                </div>
            </div>

            {/* search bar */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                maxWidth: '300px',
                marginLeft: Spacing.SCALE_16,
                marginRight: Spacing.SCALE_16,
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    backgroundColor: usedTheme.BACKGROUND_COLOR,
                    border: `1px solid ${usedTheme.BORDER_COLOR}`,
                    borderRadius: '6px',
                    padding: '6px 12px',
                }}>
                    <FaSearch
                        color={usedTheme.TEXT_COLOR}
                        size={14}
                        style={{ opacity: 0.5, marginRight: Spacing.SCALE_8 }}
                    />
                    <input
                        type="text"
                        placeholder="Search flights..."
                        value={searchTerm}
                        onChange={(e) => {
                            dispatch(setFlightSearchTerm(e.target.value));
                        }}
                        style={{
                            border: 'none',
                            outline: 'none',
                            backgroundColor: 'transparent',
                            color: usedTheme.TEXT_COLOR,
                            fontSize: '14px',
                            width: '100%',
                        }}
                    />
                </div>
                {searchTerm && (
                    <button
                        onClick={() => dispatch(setFlightSearchTerm(''))}
                        style={{
                            marginLeft: Spacing.SCALE_8,
                            padding: '4px 10px',
                            border: `1px solid ${Colors.PRM_LIGHT_BLUE}`,
                            borderRadius: '6px',
                            backgroundColor: Colors.PRM_LIGHT_BLUE,
                            color: '#fff',
                            fontSize: '12px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Clear
                    </button>
                )}
            </div>

            {/* filter and actions */}
            <div style={{ display: 'flex', flex: 1, justifyContent: 'flex-end', alignItems: 'center', gap: Spacing.SCALE_8, zIndex: 1000 }}>
                {props?.hideFlightFilter ? null :
                    <>
                        {/* select job filter */}
                        <div data-tippy-content="Filter by flight" style={{ minWidth: '200px', maxWidth: '300px' }}>
                            <Select
                                options={bookingsTypes}
                                placeholder="Displayed bookings..."
                                onChange={(selection) => changeBookingTypeFilter(selection)}
                                value={createBookingTypeFilterObj()}
                                isDisabled={selectedTimeline?.arrivals === true && selectedTimeline?.departures !== true}
                                isMulti={true}
                                onMenuClose={() => {
                                    console.log("menu closed", bookingType);
                                    props?.handleGetFlights(bookingType);
                                    props?.handleGetBookings(bookingType);

                                }}
                                closeMenuOnSelect={false}
                                // closeMenuOnClickOutside={false}
                                // closeMenuOnClear={true}
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        backgroundColor: usedTheme.BACKGROUND_COLOR,
                                        borderColor: usedTheme.BORDER_COLOR,
                                        color: usedTheme.TEXT_COLOR,
                                        minHeight: '38px',
                                        maxHeight: '38px',
                                        overflow: 'hidden',
                                    }),
                                    valueContainer: (provided) => ({
                                        ...provided,
                                        flexWrap: 'nowrap',
                                        overflow: 'hidden',
                                    }),
                                    placeholder: (provided) => ({
                                        ...provided,
                                        color: usedTheme.TEXT_COLOR,
                                    }),
                                    singleValue: (provided) => ({
                                        ...provided,
                                        color: usedTheme.TEXT_COLOR,
                                    }),
                                    multiValue: (provided) => ({
                                        ...provided,
                                        backgroundColor: usedTheme.BORDER_COLOR,
                                    }),
                                    multiValueLabel: (provided) => ({
                                        ...provided,
                                        color: usedTheme.TEXT_COLOR,
                                    }),
                                    multiValueRemove: (provided) => ({
                                        ...provided,
                                        color: usedTheme.TEXT_COLOR,
                                        ':hover': {
                                            backgroundColor: usedTheme.BORDER_COLOR,
                                            color: usedTheme.TEXT_COLOR,
                                        },
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        zIndex: 4,
                                        backgroundColor: usedTheme.BACKGROUND_COLOR,
                                        borderColor: usedTheme.BORDER_COLOR,
                                        color: usedTheme.TEXT_COLOR
                                    }),
                                    option: (provided, state) => ({
                                        ...provided,
                                        backgroundColor: state.isFocused
                                            ? usedTheme.BORDER_COLOR
                                            : usedTheme.BACKGROUND_COLOR,
                                        color: usedTheme.TEXT_COLOR,
                                        cursor: 'pointer',
                                    }),
                                }}
                            />
                        </div>


                        {/* action buttons group */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.SCALE_8 }}>
                            {/* add pax button */}
                            <Button
                                variant="primary"
                                onClick={() => { props.setShowCreateModal(true) }}
                                data-tippy-content="Add New Passenger"
                            >
                                <FaPlus color="white" size={Spacing.SCALE_16} />
                            </Button>

                            {/* refresh button */}
                            <Button
                                variant="primary"
                                data-tippy-content="Refresh Feed"
                                onClick={() => {
                                    props?.handleGetBookings(bookingType);
                                    props?.handleGetFlights(bookingType);
                                }}
                            >
                                {(timelineFlightListStatus === "loading" || timelineBookingsStatus === "loading") ? (
                                    <CustomSpinner size="small" variant="light" />
                                ) : (
                                    <FiRefreshCcw color="white" size={Spacing.SCALE_16} />
                                )}
                            </Button>

                            {/* flight events button */}
                            {dbUser?.details?.authority === 'BFS' && (
                                <Button
                                    variant="primary"
                                    onClick={() => setShowFlightEventsModal(true)}
                                    data-tippy-content="Flight Events"
                                >
                                    <BsAirplane color="white" size={Spacing.SCALE_16} />
                                </Button>
                            )}


                            {/* timeline lookup button - only for BFS */}
                            <Button
                                variant="primary"
                                onClick={handleOpenTimelineSearch}
                                data-tippy-content="Timeline Lookup"
                            >
                                <FaSearch color="white" size={Spacing.SCALE_16} />
                            </Button>

                            {/* load previous hour button */}
                            <Button
                                variant="primary"
                                onClick={() => { dispatch(increaseTimelineStart(1)) }}
                                data-tippy-content="Load Previous Hour"
                                disabled={startLimit}
                            >
                                <FaChevronLeft color="white" size={Spacing.SCALE_16} />
                            </Button>

                            {/* load next hour button */}
                            <Button
                                variant="primary"
                                onClick={() => {
                                    dispatch(increaseTimelineSlots({
                                        hoursToAdd: 1,
                                        authority: dbUser?.details?.authority
                                    }))
                                }}
                                data-tippy-content="Load Next Hour"
                                disabled={endLimit}
                            >
                                <FaChevronRight color="white" size={Spacing.SCALE_16} />
                            </Button>
                        </div>
                    </>
                }
            </div>

            <ActionsModal
                isTimeline={true}
                show={showAddModal}
                handleClose={handleCloseModal}
                title="Create Employee Account"
                subtitle={"To create an employee account enter the information below and click confirm"}
                minWidth="445px"
                maxWidth="445px"
            >
                <AddEmployeeForm closeModal={handleCloseModal} />
            </ActionsModal>

            <ActionsModal
                isTimeline={true}
                show={showTimelineSearchModal}
                handleClose={handleCloseTimelineSearchModal}
                title="Timeline Search"
                subtitle={"Search for flights and passengers"}
                minWidth="800px"
                maxWidth="800px"
                minHeight="700px"
                maxHeight="700px"
            >
                <div style={{ height: '600px' }}>
                    <TimelineSearch
                        onFlightSelect={handleFlightSelect}
                        onPassengerSelect={handlePassengerSelect}
                    />
                </div>
            </ActionsModal>

            <ActionsModal
                isTimeline={true}
                show={showFlightEventsModal}
                handleClose={() => setShowFlightEventsModal(false)}
                title="Flight Events"
                subtitle={"View and manage flight-related events"}
                minWidth="800px"
                maxWidth="800px"
                minHeight="700px"
                maxHeight="700px"
            >
                <div style={{ height: '600px' }}>
                    <FlightEvents onClose={() => setShowFlightEventsModal(false)} />
                </div>
            </ActionsModal>

        </div>
    )

};

export default JobListToolbar;

